import type { RescheduleSite } from '../../hooks/reschedule-api';
import type { Media } from '../../types/media';
import { getMediaImageUrl, getPrimaryImage } from '../../util/media';

/**
 * A photographer's photo URL, sized the way the team-members grid sizes it
 * (`team-members-grid.tsx`: the `Media` component with `responsive={false}` and 120 x 120, which
 * is the primary image through `getMediaImageUrl`). Undefined with no photo, so a card falls back
 * to initials.
 */
export function teamPhotoUrl(media: Media | null | undefined): string | undefined {
    if (!media || !Array.isArray(media.images)) return undefined;
    const image = getPrimaryImage(media);
    return image?.url ? getMediaImageUrl(image, { width: 120, height: 120 }) : undefined;
}

/**
 * Appointment times on the reschedule routes are the BRAND'S WALL CLOCK, `YYYY-MM-DD HH:mm:ss`
 * with no zone — legacy `dtShoot`. They are shown and sent exactly as written: parsing one with
 * `new Date(...)` would re-read it in the visitor's own zone and move the appointment for anyone
 * not in the brand's.
 */

interface WallClock {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
}

function parseWallClock(value: string | null | undefined): WallClock | undefined {
    const match = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/.exec(value ?? '');
    if (!match) return undefined;
    return {
        year: Number(match[1]),
        month: Number(match[2]),
        day: Number(match[3]),
        hours: Number(match[4] ?? 0),
        minutes: Number(match[5] ?? 0)
    };
}

/** `Monday, April 5, 2027` — legacy `FormatDateTime(dtShoot, 1)`. */
export function formatAppointmentDate(value: string | null | undefined): string | undefined {
    const clock = parseWallClock(value);
    if (!clock) return undefined;
    // Built and read in UTC so the calendar date survives whatever zone the browser is in.
    return new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        year: 'numeric',
        timeZone: 'UTC'
    }).format(new Date(Date.UTC(clock.year, clock.month - 1, clock.day)));
}

/** `10:00 AM` — legacy `FormatDateTime(dtShoot, 3)` with the seconds dropped. */
export function formatAppointmentTime(value: string | null | undefined): string | undefined {
    const clock = parseWallClock(value);
    if (!clock) return undefined;
    const hour12 = ((clock.hours + 11) % 12) + 1;
    const minutes = String(clock.minutes).padStart(2, '0');
    return `${hour12}:${minutes} ${clock.hours < 12 ? 'AM' : 'PM'}`;
}

/**
 * The `shootDate` a save sends. A date-only pick (`minute: null`) goes out at midnight, as legacy
 * does (`cart-gcal.js` :866 stores `<date>:0`, which `reschedule.asp` :376 turns into `0:00`).
 */
export function toShootDate(date: string, minute: number | null): string {
    const total = minute ?? 0;
    const hours = String(Math.floor(total / 60)).padStart(2, '0');
    const minutes = String(total % 60).padStart(2, '0');
    return `${date} ${hours}:${minutes}:00`;
}

/** The address as legacy shows it: address, address 2, city, state — the blanks dropped. */
export function formatSiteAddress(site: RescheduleSite | undefined): string {
    return [site?.address, site?.address2, site?.city, site?.state]
        .map(part => (part ?? '').trim())
        .filter(part => part.length > 0)
        .join(', ');
}

/**
 * The brand's "today", `YYYY-MM-DD`. `timezoneOffset` is host info's (the brand's `nTimeAdjust`,
 * hours from UTC) — the same clock the API's availability and reschedule-window rules run on.
 */
export function brandToday(timezoneOffset: number | undefined, now: number = Date.now()): string {
    return new Date(now + (timezoneOffset ?? 0) * 60 * 60 * 1000).toISOString().slice(0, 10);
}

/** `14:05` for a hold's remaining time. */
export function formatCountdown(msRemaining: number): string {
    const seconds = Math.max(Math.ceil(msRemaining / 1000), 0);
    return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}
